/**
 * The Fresh AI texts lever (user, 15/10/2026: "plain turns through the
 * prompt groups — I can't understand it"): two plain choices, then one bar
 * per prompt group — how many of the drops get their AI texts from it — that
 * slides when the choice flips. The made-up groups carry an Example tag.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { FlaskConical } from 'lucide-react';

import { ChoiceChips } from '../../../../general/controls/ChoiceChips';
import { leanRows, type LeanGroup, type LeanRow, type LeanView } from './lean-rows';
import { TurnStrip } from './TurnStrip';

/** Shown until the container has prompt groups, so the two choices are visible. */
const DEMO: LeanGroup[] = [
  { id: 'demo-warm', name: 'Warm welcome', score: 1.6 },
  { id: 'demo-bonus', name: 'Bonus reminder', score: 0.9 },
  { id: 'demo-last', name: 'Last chance', score: 0.4 },
];

type Mode = 'turns' | 'lean';

const OPTIONS = [
  { value: 'turns', label: 'Every group equally' },
  { value: 'lean', label: 'Best groups more often' },
] as const;

const fmtScore = (s: number | null) => (s === null ? 'no results yet' : `score ${s.toFixed(2)}`);

const Bars: FC<{ rows: LeanRow[]; lean: boolean; drops: number }> = ({ rows, lean, drops }) => (
  <div className="grid grid-cols-[minmax(0,13rem)_minmax(4rem,1fr)_auto] items-center gap-x-3 gap-y-1.5" data-testid="lean-bars">
    {rows.map((r) => {
      const n = lean ? r.lean : r.turns;
      return (
        <div key={r.id} className="contents" data-testid={`lean-${r.id}`}>
          <span className="truncate text-xs" title={r.name}>
            {r.name}
          </span>
          <span className="h-2 overflow-hidden rounded-full bg-muted">
            <span className="block h-full rounded-full bg-primary/70 transition-[width] duration-500 ease-out motion-reduce:transition-none" style={{ width: `${drops > 0 ? (n / drops) * 100 : 0}%` }} />
          </span>
          <span className="whitespace-nowrap text-right text-[11px] tabular-nums text-muted-foreground">
            <b className="font-semibold text-foreground">{n}</b> drop{n === 1 ? '' : 's'} · {fmtScore(r.score)}
          </span>
        </div>
      );
    })}
  </div>
);

function caption(view: LeanView, lean: boolean, rows: LeanRow[]): string {
  const day = view.today ? `today's ${view.drops} drops with fresh AI texts` : `a day of ${view.drops} drops`;
  const flat = rows.every((r) => r.lean === r.turns);
  if (lean && flat && rows.every((r) => r.score === null)) return `Out of ${day} — no group has results yet, so they take turns until one does.`;
  return `Out of ${day} — each drop's AI texts are written from one prompt group.`;
}

interface Props {
  lean: boolean;
  onLean: (lean: boolean) => void;
  view: LeanView;
  /** The ordinary drop's ✦ count — 0 = no fresh text to share out. */
  aiPerDrop: number;
}

export const LeanPreview: FC<Props> = ({ lean, onLean, view, aiPerDrop }) => {
  const own = leanRows(view.groups, view.drops);
  const demo = own.length === 0 && aiPerDrop > 0;
  const rows = demo ? leanRows(DEMO, 12) : own;
  const drops = demo ? 12 : view.drops;
  const tag = demo ? 'Example · pick your groups and this uses them' : 'Example · made-up groups — yours have no results yet';
  return (
    <div className="flex flex-col gap-2">
      <ChoiceChips<Mode> options={OPTIONS} value={lean ? 'lean' : 'turns'} onChange={(m) => onLean(m === 'lean')} ariaLabel="Which prompt groups write the fresh AI texts" />
      {aiPerDrop <= 0 ? (
        <p className="text-[11px] text-muted-foreground">No ordinary drop has fresh AI texts (✦ 0 above) — nothing to share out.</p>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/20 p-3">
          {demo || view.example ? (
            <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-primary" data-testid="lean-example">
              <FlaskConical className="h-3 w-3" aria-hidden />
              {tag}
            </span>
          ) : null}
          <TurnStrip rows={rows} lean={lean} drops={drops} />
          <p className="text-[11px] text-muted-foreground">Each square is the next drop. The colour is the prompt group that writes its texts.</p>
          <Bars rows={rows} lean={lean} drops={drops} />
          <p className="text-[11px] text-muted-foreground">{demo ? 'Switch the choice and watch the squares. Equal turns, or the higher scores more often.' : caption(view, lean, rows)}</p>
        </div>
      )}
    </div>
  );
};
